import React, { useState, useMemo } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, IconButton, TextField, Button, Alert,
  Paper, CircularProgress, MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ReturnIcon,
  LocalShipping as ShipIcon,
} from '@mui/icons-material';
import { bulkReturn, bulkDirectShipment } from '../utils/gateoutApi';

// ============================================================================
//  BulkProcessModal — Phase 2B Step 1.5 + Step 2
//
//  Shared component for bulk return / direct shipment from CS Out tabs.
//  Auto-detects whole vs split per pallet based on the take-cartons input
//  the user adjusted on the parent page.
//
//  Props:
//    open            — bool, controls Dialog visibility
//    action          — 'return' | 'shipment'
//    tab             — { id, label, color, returnTarget, statuses, ... }
//    pallets         — array of inventory rows currently displayed
//    selection       — Map<inventory.id, { cartons }>
//    activePalletIds — Set<string>, all in-use pallet IDs (for uniqueness check)
//    onClose         — () => void
//    onSuccess       — (result) => void  (closes modal, parent refetches)
// ============================================================================
export default function BulkProcessModal({
  open, action, tab, pallets, selection, activePalletIds,
  onClose, onSuccess,
}) {
  const initialEntries = useMemo(() => {
    if (!selection) return [];
    const arr = [];
    for (const [id, sel] of selection.entries()) {
      const p = pallets.find(x => x.id === id);
      if (!p) continue;
      arr.push({
        palletId:     id,
        sourcePallet: p,
        cartons:      sel.cartons,
        isSplit:      sel.cartons < parseInt(p.cartons, 10),
        newPalletId:  '',
        bin:          '',
      });
    }
    return arr;
  }, [pallets, selection]);

  const [entries, setEntries] = useState(initialEntries);
  const [returnDate, setReturnDate] = useState(new Date().toISOString().slice(0, 10));
  const [fcl, setFcl] = useState({
    fcl_no: '', fcl_month: new Date().toISOString().slice(0, 7),
    container_no: '', seal_no: '',
    buyer: '', destination_port: '',
    stuffing_location: 'BAPATLA - GATE 1',
    departure_date: new Date().toISOString().slice(0, 10),
    departure_time: '18:00',
    vehicle_no: '', driver_name: '', driver_mobile: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  React.useEffect(() => {
    if (open) {
      setEntries(initialEntries);
      setSubmitError('');
    }
  }, [open, initialEntries]);

  const setEntry = (palletId, patch) =>
    setEntries(prev => prev.map(e => e.palletId === palletId ? { ...e, ...patch } : e));

  const totals = useMemo(() => {
    let cartons = 0, kgs = 0;
    entries.forEach(e => {
      const p = e.sourcePallet;
      const orig = parseInt(p.cartons, 10);
      const cartonKg = orig > 0 ? parseFloat(p.total_kgs) / orig : 0;
      cartons += e.cartons;
      kgs += cartonKg * e.cartons;
    });
    return { cartons, kgs, lbs: kgs * 2.20 };
  }, [entries]);

  const rowErrors = useMemo(() => {
    const m = {};
    const enteredIds = new Map();
    entries.forEach(e => {
      const errs = [];
      if (e.isSplit) {
        const newId = e.newPalletId.trim();
        if (action === 'return') {
          // Returns: new pallet ID is REQUIRED and must be UNIQUE
          // (two physical pallets exist in CS at the same time)
          if (!newId) {
            errs.push('Pallet ID required for split');
          } else if (activePalletIds && activePalletIds.has(newId)) {
            errs.push(`Pallet ID ${newId} already in use`);
          } else if (enteredIds.has(newId)) {
            errs.push(`Pallet ID ${newId} duplicated in this form`);
          } else {
            enteredIds.set(newId, e.palletId);
          }
        } else if (action === 'shipment') {
          // Shipments: new pallet ID is OPTIONAL — only cartons leave the
          // facility, the physical pallet stays in CS. Default = source's
          // pallet_id. No uniqueness check.
          // (validation passes regardless of value)
        }
      }
      if (action === 'return' && !e.bin.trim()) {
        errs.push('BIN location required');
      }
      m[e.palletId] = errs;
    });
    return m;
  }, [entries, activePalletIds, action]);

  const fclErrors = useMemo(() => {
    if (action !== 'shipment') return [];
    const errs = [];
    if (!fcl.fcl_no.trim())            errs.push('FCL No required');
    if (!fcl.container_no.trim())      errs.push('Container No required');
    if (!fcl.buyer.trim())             errs.push('Buyer required');
    if (!fcl.destination_port.trim())  errs.push('Destination port required');
    return errs;
  }, [fcl, action]);

  const allErrors = useMemo(() => {
    const all = [];
    Object.values(rowErrors).forEach(errs => errs.forEach(e => all.push(e)));
    fclErrors.forEach(e => all.push(e));
    return [...new Set(all)];
  }, [rowErrors, fclErrors]);

  const canSubmit = !submitting && allErrors.length === 0 && entries.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const apiEntries = entries.map(e => ({
        pallet_id:     e.palletId,
        cartons:       e.cartons,
        new_pallet_id: e.isSplit ? e.newPalletId.trim() : '',
        bin:           e.bin.trim(),
      }));

      const res = action === 'return'
        ? await bulkReturn(apiEntries, returnDate)
        : await bulkDirectShipment(apiEntries, fcl);

      if (!res.success) {
        setSubmitError(res.error || 'Submission failed');
      } else {
        onSuccess(res);
      }
    } catch (err) {
      setSubmitError(err.response?.data?.error || err.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  const isShipment = action === 'shipment';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{
        background: isShipment
          ? 'linear-gradient(90deg, #5b21b6, #8b5cf6)'
          : 'linear-gradient(90deg, #1d4ed8, #3b82f6)',
        color: 'white', py: 1.5, px: 2.5,
        display: 'flex', alignItems: 'center', gap: 1,
      }}>
        {isShipment ? <ShipIcon /> : <ReturnIcon />}
        <Typography sx={{ fontSize: 15, fontWeight: 700, flex: 1 }}>
          {isShipment ? 'Direct Shipment' : `Return to CS as ${tab.returnTarget}`}
          {' · '}from {tab.label}
          {' · '}{entries.length} pallets · {totals.cartons} cartons
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5 }}>
        <Typography sx={{
          fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: '#475569',
          textTransform: 'uppercase', mb: 1, mt: 1.5,
        }}>
          Pallets being {isShipment ? 'shipped' : `returned (will become ${tab.returnTarget})`}
        </Typography>

        <Paper variant="outlined" sx={{ p: 1, bgcolor: '#fafbfc' }}>
          {entries.map(e => (
            <PalletCard
              key={e.palletId}
              entry={e}
              action={action}
              sourceStatus={tab.statuses[0]}
              errors={rowErrors[e.palletId]}
              onChange={(patch) => setEntry(e.palletId, patch)}
            />
          ))}
        </Paper>

        {/* Totals bar */}
        <Box sx={{
          mt: 1.5, p: 1.25,
          bgcolor: '#f5edf7', border: '1px solid #c4b5fd', borderRadius: 1,
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
          fontSize: 13,
        }}>
          <span style={{ fontSize: 18 }}>📊</span>
          <span>
            <strong>{entries.length}</strong> pallets ·{' '}
            <strong style={{ fontFamily: 'JetBrains Mono, monospace', color: '#5b21b6' }}>
              {totals.cartons}
            </strong> cartons ·{' '}
            <strong style={{ fontFamily: 'JetBrains Mono, monospace', color: '#5b21b6' }}>
              {totals.kgs.toFixed(2)} KG
            </strong> /{' '}
            <strong style={{ fontFamily: 'JetBrains Mono, monospace', color: '#5b21b6' }}>
              {totals.lbs.toFixed(2)} LB
            </strong>
          </span>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#64748b' }}>
            {entries.filter(x => x.isSplit).length} split ·{' '}
            {entries.filter(x => !x.isSplit).length} whole
          </span>
        </Box>

        {/* Return-specific shared section */}
        {action === 'return' && (
          <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5, bgcolor: '#eff6ff', borderColor: '#93c5fd' }}>
            <Typography sx={{
              fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: '#1d4ed8',
              textTransform: 'uppercase', mb: 1,
            }}>
              ↩ Return Details
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
              <TextField
                label="Return Date" type="date" size="small"
                value={returnDate}
                onChange={(e) => setReturnDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Typography sx={{ fontSize: 11, color: '#1d4ed8', mt: 1 }}>
              ℹ️ All selected pallets will move to <strong>{tab.returnTarget}</strong> status
              and reappear on Cold Store IN at the BIN locations specified above.
            </Typography>
          </Paper>
        )}

        {/* Shipment-specific FCL section */}
        {action === 'shipment' && (
          <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5, bgcolor: '#f5f3ff', borderColor: '#c4b5fd' }}>
            <Typography sx={{
              fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: '#6d28d9',
              textTransform: 'uppercase', mb: 1,
            }}>
              🚚 Shipment Details (one FCL doc covers all selected pallets)
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
              <TextField label="FCL No *" size="small"
                value={fcl.fcl_no}
                onChange={(e) => setFcl({...fcl, fcl_no: e.target.value})}
                placeholder="FCL-2026-1024"
                error={!fcl.fcl_no.trim()} />
              <TextField label="FCL Month" type="month" size="small"
                value={fcl.fcl_month}
                onChange={(e) => setFcl({...fcl, fcl_month: e.target.value})}
                InputLabelProps={{ shrink: true }} />
              <TextField label="Container No *" size="small"
                value={fcl.container_no}
                onChange={(e) => setFcl({...fcl, container_no: e.target.value})}
                placeholder="MSCU 1234567"
                error={!fcl.container_no.trim()} />
              <TextField label="Seal No" size="small"
                value={fcl.seal_no}
                onChange={(e) => setFcl({...fcl, seal_no: e.target.value})}
                placeholder="SE-998877" />
              <TextField label="Buyer *" size="small" select
                value={fcl.buyer}
                onChange={(e) => setFcl({...fcl, buyer: e.target.value})}
                error={!fcl.buyer.trim()}>
                <MenuItem value="">— select —</MenuItem>
                <MenuItem value="BOWL AND BASKET">BOWL AND BASKET</MenuItem>
                <MenuItem value="WHOLESOME PANTRY">WHOLESOME PANTRY</MenuItem>
                <MenuItem value="GREAT CATCH">GREAT CATCH</MenuItem>
                <MenuItem value="TC GOLD STAR">TC GOLD STAR</MenuItem>
                <MenuItem value="OTHER">OTHER</MenuItem>
              </TextField>
              <TextField label="Destination Port *" size="small"
                value={fcl.destination_port}
                onChange={(e) => setFcl({...fcl, destination_port: e.target.value})}
                placeholder="NEW YORK"
                error={!fcl.destination_port.trim()} />
              <TextField label="Vehicle / Truck No" size="small"
                value={fcl.vehicle_no}
                onChange={(e) => setFcl({...fcl, vehicle_no: e.target.value})}
                placeholder="AP-39-XX-1234" />
              <TextField label="Driver Name" size="small"
                value={fcl.driver_name}
                onChange={(e) => setFcl({...fcl, driver_name: e.target.value})} />
              <TextField label="Driver Mobile" size="small"
                value={fcl.driver_mobile}
                onChange={(e) => setFcl({...fcl, driver_mobile: e.target.value})} />
              <TextField label="Departure Date" type="date" size="small"
                value={fcl.departure_date}
                onChange={(e) => setFcl({...fcl, departure_date: e.target.value})}
                InputLabelProps={{ shrink: true }} />
              <TextField label="Departure Time" type="time" size="small"
                value={fcl.departure_time}
                onChange={(e) => setFcl({...fcl, departure_time: e.target.value})}
                InputLabelProps={{ shrink: true }} />
              <TextField label="Stuffing Location" size="small"
                value={fcl.stuffing_location}
                onChange={(e) => setFcl({...fcl, stuffing_location: e.target.value})} />
            </Box>
            <Typography sx={{ fontSize: 11, color: '#6d28d9', mt: 1 }}>
              ℹ️ Pallets will move to <strong>SHIPMENT-PENDING</strong> status. The shipment
              will require email approval before it becomes SHIPPED. Each pallet's
              <code style={{ background: '#ede9fe', padding: '1px 4px', borderRadius: 3, marginLeft: 4 }}>
                prior_gateout_no
              </code> preserves the parent {tab.docPrefix} reference for the audit chain.
            </Typography>
          </Paper>
        )}

        {/* Validation errors */}
        {allErrors.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1.5, fontSize: 12 }}>
            <strong>Cannot submit yet — {allErrors.length} issue{allErrors.length === 1 ? '' : 's'}:</strong>
            <ul style={{ marginLeft: 16, marginTop: 4 }}>
              {allErrors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </Alert>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mt: 1.5, fontSize: 12 }}>{submitError}</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ bgcolor: '#fafbfc', borderTop: '1px solid #e2e8f0', p: 1.5 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={handleSubmit}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            background: 'linear-gradient(90deg, #C0392B, #8E44AD, #1F618D)',
            color: 'white', fontWeight: 700,
            '&:hover': { background: 'linear-gradient(90deg, #a8302a, #7a3a96, #1a557a)' },
            '&.Mui-disabled': { opacity: 0.4, color: 'white' },
          }}
        >
          {submitting
            ? 'Submitting...'
            : isShipment
              ? `📨 Submit Shipment (${entries.length} pallets, email approval)`
              : `↩ Return ${entries.length} pallets to CS`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
//  Pallet Card sub-component
// ============================================================================
function PalletCard({ entry, action, sourceStatus, errors, onChange }) {
  const e = entry;
  const isShipment = action === 'shipment';
  const orig = parseInt(e.sourcePallet.cartons, 10);
  const cartonKg = orig > 0 ? parseFloat(e.sourcePallet.total_kgs) / orig : 0;
  const takeKg = cartonKg * e.cartons;

  return (
    <Box sx={{
      bgcolor: 'white',
      border: '1px solid #e2e8f0',
      borderLeft: `4px solid ${isShipment ? '#7c3aed' : '#2563eb'}`,
      borderRadius: 1,
      p: 1.5, mb: 1,
      '&:last-child': { mb: 0 },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#1F618D', fontSize: 14 }}>
          {e.sourcePallet.pallet_id}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
          from{' '}
          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700 }}>
            {e.sourcePallet.gateout_no || '—'}
          </span>
          {' · '}{e.sourcePallet.product} · {e.sourcePallet.grade} · {e.sourcePallet.pack_size}
          {' · '}Lot {e.sourcePallet.lot_no}
        </Typography>
        <Box sx={{
          marginLeft: 'auto',
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 11.5, color: '#64748b',
        }}>
          {e.cartons} of {orig} ctn ·{' '}
          {takeKg.toFixed(2)} KG / {(takeKg * 2.20).toFixed(2)} LB
        </Box>
        {e.isSplit ? (
          <Box sx={{ bgcolor: '#fef3c7', color: '#92400e', px: 1, py: 0.25, borderRadius: 0.5, fontSize: 10.5, fontWeight: 700 }}>
            ⚠ SPLIT — {orig - e.cartons} cartons remain in {sourceStatus}
          </Box>
        ) : (
          <Box sx={{ bgcolor: '#dcfce7', color: '#15803d', px: 1, py: 0.25, borderRadius: 0.5, fontSize: 10.5, fontWeight: 700 }}>
            ✓ WHOLE PALLET
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25 }}>
        {e.isSplit && action === 'return' && (
          <TextField
            label="New Pallet ID *"
            placeholder="unique number, e.g. 5001"
            size="small"
            value={e.newPalletId}
            onChange={(ev) => onChange({ newPalletId: ev.target.value })}
            error={errors?.some(s => s.toLowerCase().includes('pallet id'))}
            helperText={
              errors?.find(s => s.toLowerCase().includes('pallet id'))
                || 'Must not match any active pallet ID'
            }
          />
        )}
        {e.isSplit && action === 'shipment' && (
          <TextField
            label="Pallet ID for shipment portion"
            placeholder={`(default: ${e.sourcePallet.pallet_id})`}
            size="small"
            value={e.newPalletId}
            onChange={(ev) => onChange({ newPalletId: ev.target.value })}
            helperText="Leave blank — only cartons leave the facility, original pallet stays in CS with the remainder"
          />
        )}
        {action === 'return' && (
          <TextField
            label="BIN Location *"
            placeholder="e.g. 3A1-2"
            size="small"
            value={e.bin}
            onChange={(ev) => onChange({ bin: ev.target.value })}
            error={errors?.some(s => s.toLowerCase().includes('bin'))}
            helperText={errors?.find(s => s.toLowerCase().includes('bin'))}
          />
        )}
        {!e.isSplit && action === 'shipment' && (
          <Box sx={{ gridColumn: '1 / 3', p: 0.75, fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
            Keeps original ID — same physical pallet leaves the facility
          </Box>
        )}
      </Box>
    </Box>
  );
}
